import { memo } from 'react'
import TableView from './TableView'
import GridView from './GridView'
import Pagination from './Pagination'
import { FileExplorerItem, DragDropHandlers, Pagination as PaginationData } from '@features/file-explorer/types'

interface FileExplorerContentProps {
  items: FileExplorerItem[]
  viewMode: 'table' | 'grid'
  getItemKey: (item: FileExplorerItem) => string
  onItemClick: (item: FileExplorerItem) => void
  onSelectItem: (item: FileExplorerItem, index: number, shiftKey: boolean) => void
  onDelete: (item: FileExplorerItem) => void
  onRename: (item: FileExplorerItem) => void
  onDownload: (item: FileExplorerItem) => void
  onShow: (item: FileExplorerItem) => void
  onManagePermissions: (item: FileExplorerItem) => void
  isLoading: boolean
  dragAndDrop: DragDropHandlers
  pagination: PaginationData
  perPage: number
  onPageChange: (page: number, direction: 'prev' | 'next') => void
  onPerPageChange: (perPage: number) => void
  loadingPrev: boolean
  loadingNext: boolean
  loadingPerPage: boolean
}

function FileExplorerContent({
  items,
  viewMode,
  getItemKey,
  onItemClick,
  onSelectItem,
  onDelete,
  onRename,
  onDownload,
  onShow,
  onManagePermissions,
  isLoading,
  dragAndDrop,
  pagination,
  perPage,
  onPageChange,
  onPerPageChange,
  loadingPrev,
  loadingNext,
  loadingPerPage
}: FileExplorerContentProps) {
  return (
    <>
      {/* View - Table or Grid */}
      {viewMode === 'table' ? (
        <TableView
          items={items}
          getItemKey={getItemKey}
          onItemClick={onItemClick}
          onSelectItem={onSelectItem}
          onDelete={onDelete}
          onRename={onRename}
          onDownload={onDownload}
          onShow={onShow}
          onManagePermissions={onManagePermissions}
          isLoading={isLoading}
          dragAndDrop={dragAndDrop}
        />
      ) : (
        <GridView
          items={items}
          getItemKey={getItemKey}
          onItemClick={onItemClick}
          onSelectItem={onSelectItem}
          onDelete={onDelete}
          onRename={onRename}
          onDownload={onDownload}
          onShow={onShow}
          onManagePermissions={onManagePermissions}
          isLoading={isLoading}
          dragAndDrop={dragAndDrop}
        />
      )}

      {/* Pagination */}
      <Pagination
        page={pagination.page}
        pages={pagination.pages}
        count={pagination.count}
        from={pagination.from}
        to={pagination.to}
        perPage={perPage}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
        loadingPrev={loadingPrev}
        loadingNext={loadingNext}
        loadingPerPage={loadingPerPage}
      />
    </>
  )
}

// Custom comparison to prevent unnecessary re-renders
function arePropsEqual(prevProps: FileExplorerContentProps, nextProps: FileExplorerContentProps) {
  return (
    prevProps.items === nextProps.items &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.pagination === nextProps.pagination &&
    prevProps.perPage === nextProps.perPage &&
    prevProps.loadingPrev === nextProps.loadingPrev &&
    prevProps.loadingNext === nextProps.loadingNext &&
    prevProps.loadingPerPage === nextProps.loadingPerPage &&
    prevProps.dragAndDrop.isDragging === nextProps.dragAndDrop.isDragging &&
    prevProps.dragAndDrop.dragOverTarget === nextProps.dragAndDrop.dragOverTarget
  )
}

export default memo(FileExplorerContent, arePropsEqual)
